// Vendor imports
const classNames = require('classnames')
const React = require('react')
// Shared imports
const BemEntity = require('shared/bem')
const { pluralize } = require('shared/string-utils')
// Client V2 common imports
const tag = require('client-v2/common/utils/hyperscript')
const useInfiniteScroll = require('client-v2/common/hooks/infinite-scroll')
const { CapsuleButton } = require('client-v2/common/components/buttons')
const ArticleMixButton = require('client-v2/common/containers/lightweight-engagement/components/article-mix-button')
// Container imports
const ArticleProfileCollectionsItem = require('./collections-item')

// String helpers
const people = pluralize('person', 'people')
const collections = pluralize('collection')

// BEM Block entity definition
const bem = BemEntity('ArticleProfileCollections')

function ArticleProfileCollections(props) {
  return (
    tag('section', {
      className: classNames(
        props.className,
        bem.block()
      )
    },
      tag(ArticleProfileCollectionsHeader, props),
      tag(ArticleProfileCollectionsBody, props)
    )
  )
}

if (__DEV__) {
  // Prop checking imports
  const PropTypes = require('prop-types')

  ArticleProfileCollections.propTypes = {
    // Flags
    isMoreAvailable: PropTypes.bool,
    isMoreLoading: PropTypes.bool,
    isScrollable: PropTypes.bool,
    // Values
    article: PropTypes.object.isRequired,
    className: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.object).isRequired,
    // Callbacks
    onAddClick: PropTypes.func.isRequired,
    onCollectionNavigate: PropTypes.func.isRequired,
    onLoadMore: PropTypes.func,
    onUserNavigate: PropTypes.func.isRequired
  }
}

module.exports = ArticleProfileCollections

// BEM Block entity Element components

function ArticleProfileCollectionsHeader(props) {
  const { article } = props
  const mixersCount = article['mixers_count']
  const collectionsCount = article.count
  return (
    tag('div', {
      className: bem.element('header')
    },
      tag('div', {
        className: bem.element('header-content')
      },
        props.isMoreLoading && !props.items.length
        ? tag([ 'Loading collections...' ])
        : tag([
          'Mixed by ',
          tag('span', {
            className: bem.element('header-count')
          },
            mixersCount.toLocaleString()
          ),
          ` ${people(mixersCount)} • In `,
          tag('span', {
            className: bem.element('header-count')
          },
            collectionsCount.toLocaleString()
          ),
          ` ${collections(collectionsCount)}`
        ])
      )
    )
  )
}

function ArticleProfileCollectionsBody(props) {
  if (props.items.length || props.isMoreLoading) {
    return tag(ArticleProfileCollectionsList, props)
  }
  return tag(ArticleProfileCollectionsEmptyMessage, props)
}

function ArticleProfileCollectionsList(props) {
  const { isMoreAvailable, isMoreLoading, isScrollable } = props
  const showLoadingIndicator = isMoreLoading || isMoreAvailable
  const showMoreButton = !isScrollable && isMoreAvailable
  const scrollableRef = useInfiniteScroll({
    disabled: !isScrollable || !isMoreAvailable,
    onLoadMore: props.onLoadMore
  })
  return (
    tag('div', {
      ref: scrollableRef,
      className: bem.element('list', {
        scrollable: !!isScrollable
      })
    },
      props.items.map(item => (
        tag(ArticleProfileCollectionsItem, {
          key: `${item.user.id}`,
          className: bem.element('list-item'),
          item,
          onCollectionNavigate: props.onCollectionNavigate,
          onUserNavigate: props.onUserNavigate
        })
      )),
      showLoadingIndicator && tag('div', {
        className: bem.element('loading-indicator', {
          pending: isMoreLoading
        })
      }),
      showMoreButton && tag(CapsuleButton, {
        className: bem.element('more-button'),
        onClick: props.onLoadMore
      },
        'Show more'
      )
    )
  )
}

function ArticleProfileCollectionsEmptyMessage({
  article,
  articleMixButtonAnchorId: anchorId,
  onAddClick
}) {
  // Hooks
  const handleAddClick = React.useCallback(
    event => {
      event.stopPropagation()
      onAddClick({ article, anchorId })
    },
    [ onAddClick, article, anchorId ]
  )
  // Render
  return (
    tag('div', {
      className: bem.element('empty-content')
    },
      tag('div', {
        className: bem.element('empty-heading')
      },
        'Like this post?'
      ),
      tag('div', {
        className: bem.element('empty-message')
      },
        article['mixers_count']
        ? 'Be the first to add this to a collection.'
        : 'Be the first to mix this or add it to a collection.'
      ),
      tag('div', {
        className: bem.element('empty-call-to-action')
      },
        tag(ArticleMixButton, {
          className: bem.element('empty-add-button'),
          checked: article.isMixed,
          onClick: handleAddClick
        }),
        article.isMixed
        ? 'Add this post to one of your collections'
        : 'Add this post to your profile'
      )
    )
  )
}
